
import React, { useState, useEffect } from 'react';
import { Settings, AlertCircle, ArrowRight, Mail, Play, Globe, Loader2, Database, Bot, Smartphone, Braces, Server } from 'lucide-react';
import { AppNode } from '../../../types';
import { ExpressionEngine } from '../../../utils/expressionEngine';
import { extractData } from '../../../utils/dataExtraction';

interface ServiceTaskSimulationFormProps {
  node: AppNode;
  onNext: (data: any) => void;
  variables: Record<string, any>;
}

export const ServiceTaskSimulationForm: React.FC<ServiceTaskSimulationFormProps> = ({ node, onNext, variables }) => {
  const [jsonContent, setJsonContent] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionStatus, setExecutionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const isStartEvent = node.type === 'MessageStartEvent' || node.type === 'SignalStartEvent';
  const isRest = node.type === 'ServiceTaskREST';
  const isEmail = node.type === 'ServiceTaskEmail';
  const isSMS = node.type === 'ServiceTaskSMS';
  const isDB = node.type === 'ServiceTaskDB';
  const isLLM = node.type === 'ServiceTaskLLM';
  const isMCP = node.type === 'ServiceTaskMCP';

  const label = isStartEvent ? 'Payload' : 'Mock Response';
  
  let icon = <Settings size={12} />;
  if (isStartEvent) icon = <Mail size={12} />;
  else if (isRest) icon = <Globe size={12} />;
  else if (isDB) icon = <Database size={12} />;
  else if (isLLM) icon = <Bot size={12} />;
  else if (isSMS) icon = <Smartphone size={12} />;
  else if (isMCP) icon = <Server size={12} />;

  useEffect(() => {
    // Priority: 1. Example Response from Node Config, 2. Default Mocks
    if (node.data.exampleResponse) {
        setJsonContent(node.data.exampleResponse);
    } else if (isStartEvent) {
        setJsonContent('{\n  "payloadId": "12345",\n  "data": {}\n}');
    } else {
        setJsonContent(JSON.stringify({ status: "success", data: {} }, null, 2));
    }
    setJsonError(null);
    setExecutionStatus('idle');
  }, [node.id, isStartEvent, node.data.exampleResponse]);

  // Interpolate configuration values
  const getInterpolatedConfig = () => {
    if (isRest) {
      const endpoint = ExpressionEngine.interpolate(node.data.endpoint || '', variables);
      const method = node.data.method || 'GET';
      const body = node.data.requestBody ? ExpressionEngine.interpolate(node.data.requestBody, variables) : null;
      
      // Interpolate headers
      const headers: Record<string, string> = {};
      if (node.data.headers) {
          Object.entries(node.data.headers).forEach(([k, v]) => {
              headers[k] = ExpressionEngine.interpolate(v as string, variables);
          });
      }

      return { endpoint, method, body, headers };
    }
    if (isEmail) {
      return {
        to: ExpressionEngine.interpolate(node.data.to || '', variables),
        subject: ExpressionEngine.interpolate(node.data.subject || '', variables),
        body: ExpressionEngine.interpolate(node.data.body || '', variables)
      };
    }
    if (isSMS) {
        return {
            toPhone: ExpressionEngine.interpolate(node.data.toPhone || '', variables),
            message: ExpressionEngine.interpolate(node.data.message || '', variables)
        };
    }
    if (isMCP) {
        return {
            serverUrl: ExpressionEngine.interpolate(node.data.mcpServerUrl || '', variables),
            operation: node.data.mcpOperation || 'CallTool',
            toolName: node.data.mcpToolName,
            resourceUri: node.data.mcpResourceUri ? ExpressionEngine.interpolate(node.data.mcpResourceUri, variables) : undefined
        };
    }
    // Add logic for DB/LLM if needed, but REST is the primary execution target here
    return null;
  };

  const configPreview = getInterpolatedConfig();

  const handleExecute = async () => {
      if (!isRest || !configPreview) return;
      
      setIsExecuting(true);
      setExecutionStatus('idle');

      try {
          const { endpoint, method, body, headers } = configPreview as any;
          
          const res = await fetch(endpoint, {
              method,
              headers,
              body: (method !== 'GET' && method !== 'HEAD' && body) ? body : undefined
          });

          const contentType = res.headers.get("content-type");
          let data;
          if (contentType && contentType.includes("application/json")) {
              data = await res.json();
          } else {
              data = { text: await res.text() };
          }

          // Populate mock response with actual result
          setJsonContent(JSON.stringify({
              status: res.status,
              statusText: res.statusText,
              data: data
          }, null, 2));
          
          setExecutionStatus('success');
      } catch (err: any) {
          setJsonError(`Execution failed: ${err.message}`);
          setExecutionStatus('error');
      } finally {
          setIsExecuting(false);
      }
  };

  const handleSubmit = () => {
    let extractedData = {};
    
    // 1. Perform Extraction based on Node Config Rules
    if (node.data.extractionRules && node.data.extractionRules.length > 0) {
        extractedData = extractData(jsonContent, node.data.extractionRules);
    } else {
        // Fallback: If no rules, but it is a Start Event, we usually assume the whole payload goes in
        if (isStartEvent) {
            try {
                extractedData = JSON.parse(jsonContent);
            } catch(e) {}
        } 
        // For standard services without rules, we might optionally put the whole raw string in the result variable if defined
        else if (node.data.resultVariable || node.data.outputVariable) {
             const varName = node.data.resultVariable || node.data.outputVariable;
             if (varName) {
                 // Try to parse as JSON first, else string
                 try {
                     extractedData = { [varName]: JSON.parse(jsonContent) };
                 } catch (e) {
                     extractedData = { [varName]: jsonContent };
                 }
             }
        }
    }

    // 2. For REST tasks specifically, historically we also saved 'mockResponse' for debugging or direct access
    if (isRest) {
        try {
            const parsed = JSON.parse(jsonContent);
            extractedData = { ...extractedData, mockResponse: parsed };
        } catch (e) { /* ignore */ }
    }

    onNext(extractedData);
  };

  const renderConfigPreview = () => {
    if (!configPreview) return null;

    if (isRest) {
      const { endpoint, method, headers, body } = configPreview as any;
      const methodColor = method === 'GET' ? 'text-blue-600 bg-blue-50' : method === 'POST' ? 'text-emerald-600 bg-emerald-50' : 'text-orange-600 bg-orange-50';
      
      return (
        <div className="mb-4 space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3">
             <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                   <Globe size={10} /> Request Preview
                </div>
                {isRest && (
                    <button 
                        onClick={handleExecute}
                        disabled={isExecuting}
                        className="text-[10px] flex items-center gap-1 font-bold uppercase tracking-wider bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded text-slate-600 hover:text-teal-600 hover:border-teal-500 transition-colors disabled:opacity-50"
                    >
                       {isExecuting ? <Loader2 size={10} className="animate-spin"/> : <Play size={10} />}
                       Test Live Endpoint
                    </button>
                )}
             </div>
             
             <div className="flex items-center gap-2 font-mono text-xs">
                 <span className={`px-1.5 py-0.5 rounded font-bold ${methodColor} border border-black/5`}>{method}</span>
                 <span className="truncate text-slate-700 dark:text-slate-300" title={endpoint}>{endpoint}</span>
             </div>

             {Object.keys(headers).length > 0 && (
                 <div className="text-[10px] text-slate-500 font-mono">
                     <span className="font-semibold text-slate-400">Headers: </span> 
                     {Object.keys(headers).length} defined
                 </div>
             )}

             {body && (
                 <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                     <div className="text-[10px] font-bold text-slate-400 mb-1">BODY</div>
                     <pre className="text-[10px] text-slate-600 dark:text-slate-400 font-mono whitespace-pre-wrap break-all max-h-20 overflow-y-auto">
                        {body}
                     </pre>
                 </div>
             )}
             
             {executionStatus === 'error' && (
                 <div className="mt-2 p-2 bg-red-50 text-red-600 text-[10px] rounded border border-red-100 flex items-center gap-1">
                     <AlertCircle size={10} /> Request failed. Check console or CORS settings.
                 </div>
             )}
        </div>
      );
    }
    
    if (isEmail) {
        const { to, subject } = configPreview as any;
        return (
            <div className="mb-4 space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3">
                 <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-2">
                   <Mail size={10} /> Email Preview
                 </div>
                 <div className="grid grid-cols-[40px_1fr] gap-1 text-xs">
                     <span className="text-slate-400 font-medium">To:</span>
                     <span className="text-slate-700 dark:text-slate-300 font-mono truncate">{to}</span>
                     
                     <span className="text-slate-400 font-medium">Subj:</span>
                     <span className="text-slate-700 dark:text-slate-300 font-medium truncate">{subject}</span>
                 </div>
            </div>
        );
    }

    if (isSMS) {
        const { toPhone, message } = configPreview as any;
        return (
            <div className="mb-4 space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3">
                 <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-2">
                   <Smartphone size={10} /> SMS Preview
                 </div>
                 <div className="grid grid-cols-[40px_1fr] gap-1 text-xs">
                     <span className="text-slate-400 font-medium">To:</span>
                     <span className="text-slate-700 dark:text-slate-300 font-mono truncate">{toPhone}</span>
                 </div>
                 <div className="mt-1 pt-1 border-t border-slate-100 dark:border-slate-800">
                     <div className="text-[10px] text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{message}</div>
                 </div>
            </div>
        );
    }

    if (isMCP) {
        const { serverUrl, operation, toolName, resourceUri } = configPreview as any;
        return (
            <div className="mb-4 space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3">
                 <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-2">
                   <Server size={10} /> MCP Request Preview
                 </div>
                 <div className="flex flex-col gap-1 text-xs">
                     <div className="flex gap-2 items-center">
                         <span className="text-slate-400 font-medium w-12 shrink-0">Server:</span>
                         <span className="text-slate-700 dark:text-slate-300 font-mono truncate">{serverUrl}</span>
                     </div>
                     <div className="flex gap-2 items-center">
                         <span className="text-slate-400 font-medium w-12 shrink-0">Op:</span>
                         <span className="text-teal-600 font-bold">{operation}</span>
                     </div>
                     {toolName && (
                         <div className="flex gap-2 items-center">
                             <span className="text-slate-400 font-medium w-12 shrink-0">Tool:</span>
                             <span className="text-slate-700 dark:text-slate-300 font-mono">{toolName}</span>
                         </div>
                     )}
                     {resourceUri && (
                         <div className="flex gap-2 items-center">
                             <span className="text-slate-400 font-medium w-12 shrink-0">URI:</span>
                             <span className="text-slate-700 dark:text-slate-300 font-mono truncate">{resourceUri}</span>
                         </div>
                     )}
                 </div>
            </div>
        );
    }

    return null;
  };

  const rules = node.data.extractionRules || [];

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900">
      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-4">
          
          {/* Config Preview Section */}
          {!isStartEvent && renderConfigPreview()}

          <div className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
             <div className="flex items-center gap-2">
                {icon}
                <span>{label}</span>
             </div>
             {executionStatus === 'success' && (
                 <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Updated from live request</span>
             )}
          </div>
          
          <div className="relative">
            <textarea 
                className={`w-full h-64 px-3 py-2 font-mono text-xs border rounded-lg bg-slate-50 dark:bg-slate-950 focus:ring-2 focus:ring-teal-500 outline-none resize-none ${jsonError ? 'border-red-500' : 'border-slate-300 dark:border-slate-700'}`}
                value={jsonContent}
                onChange={(e) => {
                    setJsonContent(e.target.value);
                    setJsonError(null);
                    setExecutionStatus('idle');
                }}
                spellCheck={false}
            />
            {jsonError && (
                <div className="absolute bottom-2 right-2 text-xs text-red-500 bg-white dark:bg-slate-900 px-2 py-1 rounded shadow-sm border border-red-100 dark:border-red-900 flex items-center gap-1">
                    <AlertCircle size={12}/> {jsonError}
                </div>
            )}
          </div>
          
          {rules.length > 0 && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-100 dark:border-slate-800 space-y-2">
                  <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      <Braces size={10} /> Will Extract:
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                      {rules.map((rule, idx) => (
                          <div key={idx} className="bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 text-xs flex justify-between items-center">
                              <div className="flex items-center gap-2 truncate flex-1 min-w-0">
                                  <span className="text-[9px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono uppercase text-slate-500">{rule.method}</span>
                                  <span className="text-slate-600 dark:text-slate-300 truncate font-mono" title={rule.source}>{rule.source}</span>
                              </div>
                              <span className="font-bold text-teal-600 dark:text-teal-400 flex items-center gap-1 ml-2 shrink-0">
                                  <ArrowRight size={10} /> {rule.targetVar}
                              </span>
                          </div>
                      ))}
                  </div>
              </div>
          )}
          
          {rules.length === 0 && (
            <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg text-xs text-slate-500 leading-relaxed border border-slate-100 dark:border-slate-800 italic">
                {isStartEvent 
                    ? "Entire payload will be added to variables." 
                    : "No extraction rules defined. Raw response might be ignored unless result variable is set."
                }
            </div>
          )}
        </div>
      </div>

      <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex justify-end">
        <button 
          onClick={handleSubmit}
          className="w-full px-5 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold flex items-center justify-center gap-2 shadow-sm transition-all hover:translate-y-px text-sm"
        >
          {isStartEvent ? 'Trigger Event' : 'Process Response'}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};